import { useState, useCallback } from "react";
import { format, startOfWeek, addDays, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, List, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const CAT_STYLES = {
  target: { planned: "bg-blue-500/10 text-blue-600 border-dashed", completed: "bg-blue-500/30 text-blue-700 border-solid font-medium" },
  event: { planned: "bg-purple-500/10 text-purple-600 border-dashed", completed: "bg-purple-500/30 text-purple-700 border-solid font-medium" },
  catering: { planned: "bg-orange-500/10 text-orange-600 border-dashed", completed: "bg-orange-500/30 text-orange-700 border-solid font-medium" },
};

export function WeeklyCalendar({ selectedDate, onSelectDate, activities, onAddActivity, onUpdateActivity, onActivityClick }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [month, setMonth] = useState(() => new Date());
  const [view, setView] = useState("week");
  const [dragged, setDragged] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const mStart = startOfMonth(month), mEnd = endOfMonth(month);
  const firstDow = mStart.getDay();
  const pad = firstDow === 0 ? 6 : firstDow - 1;
  const monthDays = [
    ...Array.from({ length: pad }, (_, i) => addDays(mStart, -(pad - i))),
    ...eachDayOfInterval({ start: mStart, end: mEnd }),
  ];

  const activitiesForDay = (day) =>
    activities.filter((a) => format(new Date(a.scheduled_at), "yyyy-MM-dd") === format(day, "yyyy-MM-dd"));

  const onDrop = useCallback(async (day) => {
    setDragOver(null);
    if (!dragged) return;
    const orig = new Date(dragged.scheduled_at);
    if (isSameDay(orig, day)) { setDragged(null); return; }
    const next = new Date(day);
    next.setHours(orig.getHours(), orig.getMinutes(), 0);
    try {
      await onUpdateActivity(dragged.id, { scheduled_at: next.toISOString() });
      toast.success(`Moved to ${format(day, "EEE, MMM d")}`);
    } catch { /* handled in hook */ }
    setDragged(null);
  }, [dragged, onUpdateActivity]);

  const renderCell = (day, compact = false) => {
    const items = activitiesForDay(day);
    const isSel = isSameDay(day, selectedDate);
    const isToday = isSameDay(day, new Date());
    const inMonth = isSameMonth(day, month);
    const over = dragOver && isSameDay(day, dragOver);
    return (
      <div key={day.toISOString()} data-testid={`day-${format(day, "yyyy-MM-dd")}`}
        className={cn("rounded-lg border p-2 cursor-pointer transition-all", compact ? "min-h-[80px]" : "min-h-[120px]",
          isSel && "ring-2 ring-primary border-primary", isToday && !isSel && "border-success",
          !isSel && "hover:border-primary/50", !inMonth && view === "month" && "opacity-50",
          over && "ring-2 ring-primary bg-primary/5 border-primary")}
        onClick={() => onSelectDate(day)}
        onDragOver={(e) => { e.preventDefault(); setDragOver(day); }}
        onDragLeave={() => setDragOver(null)}
        onDrop={(e) => { e.preventDefault(); onDrop(day); }}>
        <div className="flex items-center justify-between mb-1">
          <div>
            {!compact && <div className="text-xs text-muted-foreground uppercase">{format(day, "EEE")}</div>}
            <div className={cn("font-bold", compact ? "text-sm" : "text-lg", isToday && "text-primary")}>{format(day, "d")}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`add-day-${format(day, "yyyy-MM-dd")}`}
            onClick={(e) => { e.stopPropagation(); onAddActivity(day); }}><Plus className="h-3 w-3" /></Button>
        </div>
        <div className="space-y-1">
          {items.slice(0, compact ? 1 : 3).map((a) => {
            const planned = a.status === "Planned";
            const s = CAT_STYLES[a.category || "target"][planned ? "planned" : "completed"];
            return (
              <div key={a.id} draggable data-testid={`cal-activity-${a.id}`}
                onDragStart={() => setDragged(a)} onDragEnd={() => { setDragged(null); setDragOver(null); }}
                onClick={(e) => { e.stopPropagation(); onActivityClick(a); }}
                className={cn("text-xs px-1.5 py-0.5 rounded border truncate cursor-grab flex items-center gap-1 group", s)}>
                <GripVertical className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60 flex-shrink-0" />
                <span>{planned ? "○" : "●"}</span>
                <span className="truncate">{a.business_name}</span>
              </div>
            );
          })}
          {items.length > (compact ? 1 : 3) && <div className="text-xs text-muted-foreground">+{items.length - (compact ? 1 : 3)} more</div>}
        </div>
      </div>
    );
  };

  return (
    <Card className="card-shadow border-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg font-bold">Calendar</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)}>
              <ToggleGroupItem value="day" size="sm" data-testid="view-day"><List className="h-4 w-4 mr-1" />Day</ToggleGroupItem>
              <ToggleGroupItem value="week" size="sm" data-testid="view-week"><CalendarIcon className="h-4 w-4 mr-1" />Week</ToggleGroupItem>
              <ToggleGroupItem value="month" size="sm" data-testid="view-month"><CalendarIcon className="h-4 w-4 mr-1" />Month</ToggleGroupItem>
            </ToggleGroup>
            <Button variant="outline" size="sm" data-testid="cal-today"
              onClick={() => { setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 })); setMonth(new Date()); onSelectDate(new Date()); }}>Today</Button>
            <Button variant="outline" size="icon" data-testid="cal-prev"
              onClick={() => view === "month" ? setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1)) : setWeekStart(addDays(weekStart, -7))}>
              <ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium min-w-[140px] text-center">
              {view === "month" ? format(month, "MMMM yyyy") : `${format(weekStart, "MMM d")} - ${format(addDays(weekStart, 6), "MMM d, yyyy")}`}
            </span>
            <Button variant="outline" size="icon" data-testid="cal-next"
              onClick={() => view === "month" ? setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1)) : setWeekStart(addDays(weekStart, 7))}>
              <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {view === "day" && (
          <div className="space-y-3">
            {renderCell(selectedDate)}
            <h4 className="font-semibold">Activities for {format(selectedDate, "EEEE, MMMM d")}</h4>
            {activitiesForDay(selectedDate).length === 0
              ? <p className="text-muted-foreground text-sm">No activities scheduled</p>
              : activitiesForDay(selectedDate).map((a) => (
                <div key={a.id} data-testid={`day-activity-${a.id}`} className="p-3 rounded-lg bg-secondary cursor-pointer hover:bg-secondary/80" onClick={() => onActivityClick(a)}>
                  <div className="font-medium">{a.business_name}</div>
                  <div className="text-sm text-muted-foreground">{format(new Date(a.scheduled_at), "h:mm a")} · {a.status}</div>
                  {a.actions_taken?.length > 0 && <div className="text-sm text-muted-foreground mt-1">{a.actions_taken.join(", ")}</div>}
                </div>
              ))}
          </div>
        )}
        {view === "week" && <div className="grid grid-cols-7 gap-2">{weekDays.map((d) => renderCell(d))}</div>}
        {view === "month" && (
          <div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-xs font-medium text-muted-foreground text-center py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">{monthDays.map((d) => renderCell(d, true))}</div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 pt-4 border-t text-xs text-muted-foreground">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500/30 border border-blue-500/40" /><span>Marketing</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-purple-500/30 border border-purple-500/40" /><span>Event</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-orange-500/30 border border-orange-500/40" /><span>Catering</span></div>
          <div className="flex items-center gap-1 border-l pl-3"><span>○ Planned</span></div>
          <div className="flex items-center gap-1"><span>● Completed</span></div>
          <div className="flex items-center gap-1 border-l pl-3"><GripVertical className="h-3 w-3" /><span>Drag to reschedule</span></div>
        </div>
      </CardContent>
    </Card>
  );
}
